import { z } from 'zod';
import { CountryCodeSchema } from './geo';

/* ------------------------------------------------------------------ */
/* Data + insights (W19): the event pipeline (apps + server → Redis    */
/* queue → ClickHouse), the north star (WOC, OCM), 10 zone KPIs every  */
/* 5 minutes, cohort retention, A/B experiments with a holdout, the    */
/* event explorer, pipeline health and the restaurant insights in the  */
/* Partner app Hub. No personal data ever reaches ClickHouse: no       */
/* phone, name or address; customers and devices are salted hashes.    */
/* ------------------------------------------------------------------ */

const Id = z.uuid();
const isoInstant = z.iso.datetime({ offset: true });

/** Capsico OS insight screens poll at this pace. */
export const INSIGHTS_POLL_SECONDS = 60;
/** Most events in one POST /v1/events batch. */
export const TRACK_BATCH_MAX = 50;
/** Most events an app keeps offline before the oldest are dropped. */
export const TRACK_BUFFER_MAX = 200;
/** Event explorer: newest events shown at most. */
export const EVENT_EXPLORER_MAX = 1000;
/** Cohort retention: weeks since the first order (0 = the first week). */
export const COHORT_WEEKS = 12;
/** Partner insights are cached this long (seconds). */
export const PARTNER_INSIGHTS_CACHE_SECONDS = 300;

/* ---------------- tracked events ---------------- */

export const TRACK_EVENTS = [
  'app_open',
  'restaurant_view',
  'menu_view',
  'add_to_cart',
  'checkout_start',
  'order_placed',
  'order_delivered',
  'order_cancelled',
] as const;
export const TrackEventNameSchema = z.enum(TRACK_EVENTS);
export type TrackEventName = z.infer<typeof TrackEventNameSchema>;

/** Apps may send only these; the order events are emitted by the server itself. */
export const CLIENT_TRACK_EVENTS = [
  'app_open',
  'restaurant_view',
  'menu_view',
  'add_to_cart',
  'checkout_start',
] as const;
export const ClientTrackEventNameSchema = z.enum(CLIENT_TRACK_EVENTS);
export type ClientTrackEventName = z.infer<typeof ClientTrackEventNameSchema>;

export const TRACK_EVENT_LABELS: Record<TrackEventName, string> = {
  app_open: 'App opened',
  restaurant_view: 'Restaurant viewed',
  menu_view: 'Menu opened',
  add_to_cart: 'Added to cart',
  checkout_start: 'Checkout started',
  order_placed: 'Order placed',
  order_delivered: 'Order delivered',
  order_cancelled: 'Order cancelled',
};

/** The Ops funnel, app open → delivered. */
export const FUNNEL_STEPS = [
  'app_open',
  'restaurant_view',
  'menu_view',
  'add_to_cart',
  'checkout_start',
  'order_placed',
  'order_delivered',
] as const satisfies readonly TrackEventName[];

/** The restaurant funnel in the Partner app (impressions = restaurant page views). */
export const OUTLET_FUNNEL_STEPS = [
  'restaurant_view',
  'menu_view',
  'add_to_cart',
  'order_placed',
  'order_delivered',
] as const satisfies readonly TrackEventName[];

export const TRACK_APPS = ['customer', 'partner', 'server'] as const;
export const TrackAppSchema = z.enum(TRACK_APPS);
export type TrackApp = z.infer<typeof TrackAppSchema>;

export const TRACK_PLATFORMS = ['android', 'ios', 'web', 'server'] as const;
export const TrackPlatformSchema = z.enum(TRACK_PLATFORMS);
export type TrackPlatform = z.infer<typeof TrackPlatformSchema>;

/** Small, flat event properties: at most 20 keys, short strings, numbers or booleans. */
export const TrackPropsSchema = z
  .record(
    z
      .string()
      .min(1)
      .max(40)
      .regex(/^[a-z][a-z0-9_]*$/, 'Property names are lower_snake_case'),
    z.union([z.string().max(120), z.number().finite(), z.boolean()]),
  )
  .refine((value) => Object.keys(value).length <= 20, 'At most 20 properties');
export type TrackProps = z.infer<typeof TrackPropsSchema>;

/** One app event as the apps send it (POST /v1/events). */
export const TrackEventInputSchema = z.object({
  /** Random per event, created on the phone: the server and ClickHouse de-duplicate on it. */
  id: Id,
  name: ClientTrackEventNameSchema,
  at: isoInstant,
  app: z.enum(['customer', 'partner']),
  platform: z.enum(['android', 'ios', 'web']),
  appVersion: z.string().trim().max(20).default(''),
  country: CountryCodeSchema.nullable().optional(),
  townId: Id.nullable().optional(),
  /** The restaurant outlet the event is about (restaurant / menu / cart / checkout). */
  outletId: Id.nullable().optional(),
  valueMinor: z.number().int().min(0).max(100_000_000).nullable().optional(),
  props: TrackPropsSchema.optional(),
});
export type TrackEventInput = z.infer<typeof TrackEventInputSchema>;

export const TrackBatchInputSchema = z.object({
  events: z.array(TrackEventInputSchema).min(1).max(TRACK_BATCH_MAX),
});
export type TrackBatchInput = z.infer<typeof TrackBatchInputSchema>;

export const TrackBatchResultSchema = z.object({
  accepted: z.number().int().min(0),
  /** Events too old / too far in the future, or the pipeline is full. */
  rejected: z.number().int().min(0),
});
export type TrackBatchResult = z.infer<typeof TrackBatchResultSchema>;

/** One event as stored in ClickHouse (and shown in the event explorer). No PII. */
export const InsightEventSchema = z.object({
  eventId: Id,
  name: TrackEventNameSchema,
  at: isoInstant,
  app: TrackAppSchema,
  platform: TrackPlatformSchema,
  appVersion: z.string(),
  country: CountryCodeSchema.nullable(),
  townId: Id.nullable(),
  zoneId: Id.nullable(),
  /** Salted hashes (never the raw id / phone). Empty when unknown. */
  customerHash: z.string(),
  deviceHash: z.string(),
  outletId: Id.nullable(),
  orderId: Id.nullable(),
  valueMinor: z.number().int().nullable(),
  props: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
});
export type InsightEvent = z.infer<typeof InsightEventSchema>;

/* ---------------- scope + periods ---------------- */

/** Scope switcher (most specific level wins), as on every Capsico OS page. */
export const InsightsScopeQuerySchema = z.object({
  country: CountryCodeSchema.optional(),
  townId: Id.optional(),
  zoneId: Id.optional(),
});
export type InsightsScopeQuery = z.infer<typeof InsightsScopeQuerySchema>;

export const INSIGHT_PERIOD_DAYS = [1, 7, 30] as const;
export const InsightsRangeQuerySchema = InsightsScopeQuerySchema.extend({
  days: z.coerce
    .number()
    .int()
    .refine((value) => (INSIGHT_PERIOD_DAYS as readonly number[]).includes(value), {
      message: 'days is 1, 7 or 30',
    })
    .default(7),
});
export type InsightsRangeQuery = z.infer<typeof InsightsRangeQuerySchema>;

/* ---------------- north star ---------------- */

export const NorthStarPointSchema = z.object({
  day: z.iso.date(),
  /** Weekly ordering customers: distinct customers with a delivered order in the 7 days to `day`. */
  woc: z.number().int().min(0),
  /** Orders per customer per month: delivered orders ÷ ordering customers, 30 days to `day`. */
  ocm: z.number().min(0),
});
export type NorthStarPoint = z.infer<typeof NorthStarPointSchema>;

export const NorthStarSchema = z.object({
  scopeLabel: z.string(),
  current: NorthStarPointSchema.nullable(),
  /** Same measure 7 days earlier (the trend chip). */
  weekAgo: NorthStarPointSchema.nullable(),
  /** Last 30 days, oldest first. */
  trend: z.array(NorthStarPointSchema),
  computedAt: isoInstant.nullable(),
});
export type NorthStar = z.infer<typeof NorthStarSchema>;

/* ---------------- zone KPIs ---------------- */

export const CANCEL_GROUPS = ['restaurant', 'customer', 'rider', 'ops', 'payment'] as const;
export const CancelGroupSchema = z.enum(CANCEL_GROUPS);
export type CancelGroup = z.infer<typeof CancelGroupSchema>;
export const CANCEL_GROUP_LABELS: Record<CancelGroup, string> = {
  restaurant: 'Restaurant (rejected or not accepted)',
  customer: 'Customer',
  rider: 'No rider',
  ops: 'Capsico Ops',
  payment: 'Unpaid',
};

export const ZoneKpiRowSchema = z.object({
  zoneId: Id.nullable(),
  zoneName: z.string(),
  townId: Id,
  townName: z.string(),
  country: CountryCodeSchema,
  periodStart: isoInstant,
  periodEnd: isoInstant,
  orders: z.number().int().min(0),
  gmvMinor: z.number().int().min(0),
  aovMinor: z.number().int().min(0).nullable(),
  /** Delivered ÷ finished (delivered + cancelled). Null when nothing finished yet. */
  deliveredPct: z.number().min(0).max(100).nullable(),
  avgDeliveryMinutes: z.number().min(0).nullable(),
  /** Delivered after the promised time ÷ delivered. */
  latePct: z.number().min(0).max(100).nullable(),
  cancelPct: z.number().min(0).max(100).nullable(),
  cancelByGroup: z.record(CancelGroupSchema, z.number().int().min(0)),
  noRiderPct: z.number().min(0).max(100).nullable(),
  newCustomers: z.number().int().min(0),
  /** Customers in the period who had ordered before it ÷ all ordering customers. */
  repeatPct: z.number().min(0).max(100).nullable(),
  computedAt: isoInstant,
});
export type ZoneKpiRow = z.infer<typeof ZoneKpiRowSchema>;

export const ZoneKpiListSchema = z.object({
  rows: z.array(ZoneKpiRowSchema),
  computedAt: isoInstant.nullable(),
});
export type ZoneKpiList = z.infer<typeof ZoneKpiListSchema>;

/* ---------------- funnel ---------------- */

export const FunnelStepSchema = z.object({
  name: TrackEventNameSchema,
  count: z.number().int().min(0),
  /** Share of the previous step (null for the first step or when the previous is 0). */
  fromPrevPct: z.number().min(0).nullable(),
  /** Share of the first step. */
  fromFirstPct: z.number().min(0).nullable(),
});
export type FunnelStep = z.infer<typeof FunnelStepSchema>;

export const FunnelSchema = z.object({
  days: z.number().int(),
  steps: z.array(FunnelStepSchema),
  /** False when ClickHouse could not be reached (the funnel is then empty). */
  available: z.boolean(),
});
export type Funnel = z.infer<typeof FunnelSchema>;

/* ---------------- cohorts ---------------- */

export const CohortQuerySchema = z.object({ country: CountryCodeSchema.optional() });
export type CohortQuery = z.infer<typeof CohortQuerySchema>;

export const CohortRowSchema = z.object({
  /** Monday of the week of the first delivered order. */
  cohortWeek: z.iso.date(),
  size: z.number().int().min(0),
  /** Share of the cohort ordering again in week k (k = 0…11), null for weeks not reached yet. */
  retention: z.array(z.number().min(0).max(100).nullable()).length(COHORT_WEEKS),
});
export type CohortRow = z.infer<typeof CohortRowSchema>;

export const CohortTableSchema = z.object({
  rows: z.array(CohortRowSchema),
  computedAt: isoInstant.nullable(),
});
export type CohortTable = z.infer<typeof CohortTableSchema>;

/* ---------------- restaurant leaderboard ---------------- */

export const LeaderboardRowSchema = z.object({
  outletId: Id,
  outletName: z.string(),
  townName: z.string(),
  country: CountryCodeSchema,
  orders: z.number().int().min(0),
  gmvMinor: z.number().int().min(0),
  deliveredPct: z.number().min(0).max(100).nullable(),
  rejectedPct: z.number().min(0).max(100).nullable(),
  rating: z.number().min(0).max(5).nullable(),
  ratingCount: z.number().int().min(0),
});
export type LeaderboardRow = z.infer<typeof LeaderboardRowSchema>;

export const LeaderboardSchema = z.object({
  days: z.number().int(),
  rows: z.array(LeaderboardRowSchema),
});
export type Leaderboard = z.infer<typeof LeaderboardSchema>;

/* ---------------- event explorer ---------------- */

export const EventExplorerQuerySchema = z.object({
  name: TrackEventNameSchema.optional(),
  outletId: Id.optional(),
  orderId: Id.optional(),
  limit: z.coerce.number().int().min(1).max(EVENT_EXPLORER_MAX).default(200),
});
export type EventExplorerQuery = z.infer<typeof EventExplorerQuerySchema>;

export const EventExplorerSchema = z.object({
  rows: z.array(InsightEventSchema),
  available: z.boolean(),
});
export type EventExplorer = z.infer<typeof EventExplorerSchema>;

/* ---------------- pipeline health ---------------- */

export const PipelineHealthSchema = z.object({
  queueDepth: z.number().int().min(0),
  queueMax: z.number().int().min(1),
  /** Events dropped because the queue was full (all time). */
  dropped: z.number().int().min(0),
  /** Events written to ClickHouse (all time). */
  inserted: z.number().int().min(0),
  lastInsertAt: isoInstant.nullable(),
  workerHeartbeatAt: isoInstant.nullable(),
  /** Heartbeat younger than 30 s. */
  workerAlive: z.boolean(),
  lastError: z.string().nullable(),
  clickhouse: z.enum(['up', 'down']),
  lastKpiRollupAt: isoInstant.nullable(),
  lastDailyRollupAt: isoInstant.nullable(),
});
export type PipelineHealth = z.infer<typeof PipelineHealthSchema>;

/* ---------------- experiments ---------------- */

export const EXPERIMENT_STATUSES = ['draft', 'running', 'stopped'] as const;
export const ExperimentStatusSchema = z.enum(EXPERIMENT_STATUSES);
export type ExperimentStatus = z.infer<typeof ExperimentStatusSchema>;
export const EXPERIMENT_STATUS_LABELS: Record<ExperimentStatus, string> = {
  draft: 'Draft',
  running: 'Running',
  stopped: 'Stopped',
};

export const ExperimentKeySchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9_]{2,47}$/, 'Keys are 3–48 lower_snake_case characters');

export const ExperimentVariantSchema = z.object({
  key: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_]{0,23}$/, 'Variant keys are lower_snake_case'),
  /** Relative weight (1–100); the first variant is the control. */
  weight: z.number().int().min(1).max(100),
});
export type ExperimentVariant = z.infer<typeof ExperimentVariantSchema>;

const variantList = z
  .array(ExperimentVariantSchema)
  .min(2)
  .max(5)
  .refine((rows) => new Set(rows.map((row) => row.key)).size === rows.length, {
    message: 'Variant keys must be unique',
  });

export const ExperimentRowSchema = z.object({
  id: Id,
  key: ExperimentKeySchema,
  name: z.string(),
  description: z.string().nullable(),
  status: ExperimentStatusSchema,
  variants: z.array(ExperimentVariantSchema),
  /** Share of devices kept out of the experiment entirely (0–50 %). */
  holdoutPct: z.number().min(0).max(50),
  /** The event that counts as "seen the experiment". */
  exposureEvent: TrackEventNameSchema,
  /** The event that counts as converted. */
  metricEvent: TrackEventNameSchema,
  startedAt: isoInstant.nullable(),
  stoppedAt: isoInstant.nullable(),
  version: z.number().int().min(1),
  updatedAt: isoInstant,
});
export type ExperimentRow = z.infer<typeof ExperimentRowSchema>;

export const ExperimentListSchema = z.object({ rows: z.array(ExperimentRowSchema) });
export type ExperimentList = z.infer<typeof ExperimentListSchema>;

export const ExperimentInputSchema = z.object({
  key: ExperimentKeySchema,
  name: z.string().trim().min(3).max(80),
  description: z.string().trim().max(300).nullable().optional(),
  variants: variantList,
  holdoutPct: z.number().min(0).max(50).default(0),
  exposureEvent: TrackEventNameSchema.default('app_open'),
  metricEvent: TrackEventNameSchema.default('order_placed'),
});
export type ExperimentInput = z.infer<typeof ExperimentInputSchema>;

/** Draft experiments can change everything but the key; running ones only name + description. */
export const ExperimentUpdateInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  name: z.string().trim().min(3).max(80).optional(),
  description: z.string().trim().max(300).nullable().optional(),
  variants: variantList.optional(),
  holdoutPct: z.number().min(0).max(50).optional(),
  exposureEvent: TrackEventNameSchema.optional(),
  metricEvent: TrackEventNameSchema.optional(),
});
export type ExperimentUpdateInput = z.infer<typeof ExperimentUpdateInputSchema>;

export const ExperimentStatusInputSchema = z.object({
  expectedVersion: z.number().int().min(1),
  status: z.enum(['running', 'stopped']),
});
export type ExperimentStatusInput = z.infer<typeof ExperimentStatusInputSchema>;

export const ExperimentVariantResultSchema = z.object({
  key: z.string(),
  isControl: z.boolean(),
  exposed: z.number().int().min(0),
  converted: z.number().int().min(0),
  /** converted ÷ exposed, in %. */
  ratePct: z.number().min(0).max(100).nullable(),
  /** Relative lift against the control, in %. */
  liftPct: z.number().nullable(),
  /** Two-proportion z-test against the control. */
  z: z.number().nullable(),
  pValue: z.number().min(0).max(1).nullable(),
  significant: z.boolean(),
});
export type ExperimentVariantResult = z.infer<typeof ExperimentVariantResultSchema>;

export const ExperimentResultsSchema = z.object({
  experimentId: Id,
  key: z.string(),
  since: isoInstant.nullable(),
  holdout: z.object({ exposed: z.number().int().min(0), converted: z.number().int().min(0) }),
  variants: z.array(ExperimentVariantResultSchema),
  /** False when ClickHouse could not be reached. */
  available: z.boolean(),
  /** Devices counted (the scan is bounded; true when the cap was hit). */
  capped: z.boolean(),
});
export type ExperimentResults = z.infer<typeof ExperimentResultsSchema>;

/** GET /v1/experiments/assignments — the device's variant per running experiment. */
export const ExperimentAssignmentSchema = z.object({
  key: z.string(),
  /** null when the device is in the holdout (show the default experience). */
  variant: z.string().nullable(),
  holdout: z.boolean(),
});
export type ExperimentAssignment = z.infer<typeof ExperimentAssignmentSchema>;

export const ExperimentAssignmentsSchema = z.object({
  assignments: z.array(ExperimentAssignmentSchema),
});
export type ExperimentAssignments = z.infer<typeof ExperimentAssignmentsSchema>;

/* ---------------- partner insights (Hub) ---------------- */

export const PARTNER_INSIGHT_PERIODS = ['today', '7d', '30d'] as const;
export const PartnerInsightPeriodSchema = z.enum(PARTNER_INSIGHT_PERIODS);
export type PartnerInsightPeriod = z.infer<typeof PartnerInsightPeriodSchema>;
export const PARTNER_INSIGHT_PERIOD_LABELS: Record<PartnerInsightPeriod, string> = {
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

export const PartnerInsightsQuerySchema = z.object({
  outletId: Id,
  period: PartnerInsightPeriodSchema.default('7d'),
});
export type PartnerInsightsQuery = z.infer<typeof PartnerInsightsQuerySchema>;

export const PartnerHourSchema = z.object({
  /** Local hour of day, 0–23. */
  hour: z.number().int().min(0).max(23),
  orders: z.number().int().min(0),
  salesMinor: z.number().int().min(0),
  lastWeekOrders: z.number().int().min(0),
  lastWeekSalesMinor: z.number().int().min(0),
});
export type PartnerHour = z.infer<typeof PartnerHourSchema>;

export const PartnerPulseSchema = z.object({
  todaySalesMinor: z.number().int().min(0),
  todayOrders: z.number().int().min(0),
  /** Same weekday last week, up to the same time of day. */
  lastWeekSalesMinor: z.number().int().min(0),
  lastWeekOrders: z.number().int().min(0),
  /** The kitchen is online right now. */
  live: z.boolean(),
  hourly: z.array(PartnerHourSchema).length(24),
});
export type PartnerPulse = z.infer<typeof PartnerPulseSchema>;

/** A number with the same measure for the period before (for the change %). */
export const PartnerMetricSchema = z.object({
  value: z.number().nullable(),
  previous: z.number().nullable(),
});
export type PartnerMetric = z.infer<typeof PartnerMetricSchema>;

export const PartnerSalesSchema = z.object({
  netSalesMinor: PartnerMetricSchema,
  ordersDelivered: PartnerMetricSchema,
  aovMinor: PartnerMetricSchema,
  /** Delivered sales per day for the sparkline, oldest first. */
  daily: z.array(z.object({ day: z.iso.date(), salesMinor: z.number().int().min(0) })),
});
export type PartnerSales = z.infer<typeof PartnerSalesSchema>;

export const PartnerHealthSchema = z.object({
  rating: z.number().min(0).max(5).nullable(),
  ratingCount: z.number().int().min(0),
  rejectionPct: PartnerMetricSchema,
  avgPrepMinutes: PartnerMetricSchema,
  /** The prep time the kitchen promised (average of accepted orders). */
  promisedPrepMinutes: z.number().min(0).nullable(),
  prepDelayedPct: PartnerMetricSchema,
  complaintsPct: PartnerMetricSchema,
  /** Share of the period the kitchen was online. */
  onlinePct: PartnerMetricSchema,
  missedOrders: PartnerMetricSchema,
});
export type PartnerHealth = z.infer<typeof PartnerHealthSchema>;

export const PartnerDishTrendSchema = z.object({
  menuItemId: Id.nullable(),
  name: z.string(),
  orders: z.number().int().min(0),
  previousOrders: z.number().int().min(0),
  trend: z.enum(['rising', 'falling', 'steady', 'new']),
  likes: z.number().int().min(0),
  dislikes: z.number().int().min(0),
});
export type PartnerDishTrend = z.infer<typeof PartnerDishTrendSchema>;

export const PartnerCustomersSchema = z.object({
  /** First order with this outlet in the period. */
  new: z.number().int().min(0),
  /** Ordered before the period and again in it. */
  repeat: z.number().int().min(0),
  /** Ordered in the period before, not in this one. */
  lapsed: z.number().int().min(0),
});
export type PartnerCustomers = z.infer<typeof PartnerCustomersSchema>;

export const PartnerInsightsSchema = z.object({
  outletId: Id,
  outletName: z.string(),
  country: CountryCodeSchema,
  timezone: z.string(),
  period: PartnerInsightPeriodSchema,
  periodStart: isoInstant,
  periodEnd: isoInstant,
  generatedAt: isoInstant,
  pulse: PartnerPulseSchema,
  sales: PartnerSalesSchema,
  health: PartnerHealthSchema,
  funnel: z.object({ steps: z.array(FunnelStepSchema), available: z.boolean() }),
  dishes: z.array(PartnerDishTrendSchema).max(10),
  customers: PartnerCustomersSchema,
});
export type PartnerInsights = z.infer<typeof PartnerInsightsSchema>;
